import { CreditTransaction, GetAdminAgentCredits200Response } from "@coeff/api";
import { DatePicker, InputNumber, message, Space, Table } from "antd";
import moment from "moment";
import React, { useState } from "react";
import styled from "styled-components";

import { DEPRECATED_Button, Form, FormItem, Input } from "../../../components";
import { COLORS } from "../../../constants";
import { useApiContext } from "../../../utils";

const CreditsForm = styled(Form)`
  && {
    padding: 30px;
    width: 600px;
    border-radius: 2px;
    box-shadow: 0 0 8px 0 rgba(188, 188, 188, 0.7);
    margin: 28px 28px;
    margin-bottom: 20px;
  }
  .ant-form-item {
    margin-bottom: 8px;
  }
  .title {
    color: ${COLORS.black85};
    font-size: 24px;
    text-align: center;
  }
`;

const BalanceCard = styled.div`
  padding: 20px 30px;
  margin: 0 28px 20px 28px;
  width: 600px;
  border-radius: 2px;
  box-shadow: 0 0 8px 0 rgba(188, 188, 188, 0.7);
  background: #f9f9f9;
`;

const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  &:last-child {
    border-bottom: none;
  }
`;

const BalanceLabel = styled.span`
  font-weight: 500;
  color: ${COLORS.black85};
`;

const BalanceValue = styled.span<{ positive?: boolean; negative?: boolean }>`
  font-weight: 600;
  color: ${props => (props.positive ? "#52c41a" : props.negative ? "#ff4d4f" : COLORS.black85)};
`;

const HistoryContainer = styled.div`
  margin: 0 28px;
  width: calc(100% - 56px);
  max-width: 900px;
`;

const SectionTitle = styled.h3`
  margin: 20px 0 10px 0;
  color: ${COLORS.black85};
`;

const historyColumns = [
  {
    title: "Date",
    dataIndex: "created_at",
    key: "created_at",
    render: (date: string) => new Date(date).toLocaleString(),
    width: 180,
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    render: (amount: number) => (
      <span style={{ color: amount >= 0 ? "#52c41a" : "#ff4d4f", fontWeight: 600 }}>
        {amount >= 0 ? `+${amount}` : amount}
      </span>
    ),
    width: 100,
  },
  {
    title: "Type",
    dataIndex: "transaction_type",
    key: "transaction_type",
    width: 120,
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
  },
];

export const Credits = () => {
  const apiContext = useApiContext();

  const [email, setEmail] = useState<string>("");
  const [creditInfo, setCreditInfo] = useState<GetAdminAgentCredits200Response | null>(null);
  const [history, setHistory] = useState<CreditTransaction[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [addAmount, setAddAmount] = useState<number | null>(null);
  const [addDescription, setAddDescription] = useState<string>("");
  const [addExpiry, setAddExpiry] = useState<Date | null>(null);

  const [lookupLoading, setLookupLoading] = useState<boolean>(false);
  const [addLoading, setAddLoading] = useState<boolean>(false);

  const validateEmail = (emailToValidate: string) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(emailToValidate).toLowerCase());
  };

  const disableLookup = () => {
    return !email || !validateEmail(email);
  };

  const disableAdd = () => {
    return !email || !validateEmail(email) || !addAmount || addAmount <= 0;
  };

  const onLookup = async () => {
    if (!email || !apiContext?.DEPRECATED_api) {
      return;
    }

    setLookupLoading(true);
    try {
      // Fetch credits
      const creditsResponse = await apiContext.DEPRECATED_api.request({
        method: "GET",
        url: `/api/admin/agent_credits?email=${encodeURIComponent(email)}`,
      });
      setCreditInfo(creditsResponse);

      // Fetch history
      const historyResponse = await apiContext.DEPRECATED_api.request({
        method: "GET",
        url: `/api/admin/agent_credits/history?email=${encodeURIComponent(email)}&limit=50`,
      });
      setHistory(historyResponse.transactions || []);
      setTotalCount(historyResponse.total_count || 0);
    } catch (error: any) {
      console.error("Credit lookup error:", error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to lookup credits";
      const statusCode = error?.response?.status;
      message.error(statusCode ? `Error ${statusCode}: ${errorMsg}` : errorMsg, 5);
      setCreditInfo(null);
      setHistory([]);
      setTotalCount(0);
    } finally {
      setLookupLoading(false);
    }
  };

  const onAddCredits = async () => {
    if (!email || !addAmount || !apiContext?.DEPRECATED_api) {
      return;
    }

    setAddLoading(true);
    try {
      const requestBody: any = {
        user_email: email,
        amount: addAmount,
      };
      if (addDescription) {
        requestBody.description = addDescription;
      }
      if (addExpiry) {
        requestBody.expiry = addExpiry.toISOString();
      }

      await apiContext.DEPRECATED_api.request({
        method: "POST",
        url: "/api/agent_credits/add",
        body: requestBody,
      });

      message.success(`Successfully added ${addAmount} credits to ${email}`);

      // Clear add form
      setAddAmount(null);
      setAddDescription("");
      setAddExpiry(null);

      // Refresh the data
      await onLookup();
    } catch (error: any) {
      console.error("Add credits error:", error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to add credits";
      const statusCode = error?.response?.status;
      message.error(statusCode ? `Error ${statusCode}: ${errorMsg}` : errorMsg, 5);
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <div style={{ padding: "1rem" }}>
      <h1 style={{ fontSize: "28px", fontWeight: 800, marginBottom: "20px" }}>Credit Management</h1>
      <CreditsForm layout="vertical">
        <FormItem label="Look up user credits by email">
          <Input
            placeholder="user@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onPressEnter={() => !disableLookup() && onLookup()}
          />
        </FormItem>

        <Space>
          <DEPRECATED_Button
            type="primary"
            disabled={disableLookup()}
            loading={lookupLoading}
            onClick={onLookup}
          >
            Lookup
          </DEPRECATED_Button>
        </Space>
      </CreditsForm>

      {creditInfo && (
        <>
          <BalanceCard>
            <SectionTitle>Credit Balance for {creditInfo.email}</SectionTitle>
            <BalanceRow>
              <BalanceLabel>Available Balance</BalanceLabel>
              <BalanceValue positive={creditInfo.balance > 0}>{creditInfo.balance}</BalanceValue>
            </BalanceRow>
            <BalanceRow>
              <BalanceLabel>On Hold</BalanceLabel>
              <BalanceValue>{creditInfo.holds}</BalanceValue>
            </BalanceRow>
            <BalanceRow>
              <BalanceLabel>Total (before holds)</BalanceLabel>
              <BalanceValue>{creditInfo.balance_before_holds}</BalanceValue>
            </BalanceRow>
            <BalanceRow>
              <BalanceLabel>Total Added (non-expired)</BalanceLabel>
              <BalanceValue positive>+{creditInfo.total_added_credits_non_expired}</BalanceValue>
            </BalanceRow>
            <BalanceRow>
              <BalanceLabel>Total Deducted (non-expired)</BalanceLabel>
              <BalanceValue negative>-{creditInfo.total_deducted_credits_non_expired}</BalanceValue>
            </BalanceRow>
          </BalanceCard>

          {creditInfo.credits_by_expiry && creditInfo.credits_by_expiry.length > 0 && (
            <BalanceCard>
              <SectionTitle>Remaining Credits by Expiry</SectionTitle>
              {creditInfo.credits_by_expiry.map((item, index) => (
                <BalanceRow key={index}>
                  <BalanceLabel>
                    {item.expiry
                      ? `Expires ${new Date(item.expiry).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}`
                      : "Never expires"}
                  </BalanceLabel>
                  <BalanceValue positive={item.credits > 0}>{item.credits}</BalanceValue>
                </BalanceRow>
              ))}
            </BalanceCard>
          )}

          <CreditsForm layout="vertical">
            <SectionTitle>Add Credits</SectionTitle>
            <FormItem label="Amount">
              <InputNumber
                min={1}
                value={addAmount}
                onChange={value => setAddAmount(value)}
                style={{ width: "100%" }}
                placeholder="Enter credit amount"
              />
            </FormItem>
            <FormItem label="Description (optional)">
              <Input
                placeholder="Reason for adding credits"
                value={addDescription}
                onChange={e => setAddDescription(e.target.value)}
              />
            </FormItem>
            <FormItem label="Expiry Date (optional)">
              <DatePicker
                showTime
                value={addExpiry ? moment(addExpiry) : null}
                onChange={(date: any) => setAddExpiry(date?.toDate() || null)}
                style={{ width: "100%" }}
                placeholder="Credits never expire if not set"
              />
            </FormItem>
            <DEPRECATED_Button
              type="primary"
              disabled={disableAdd()}
              loading={addLoading}
              onClick={onAddCredits}
            >
              Add Credits
            </DEPRECATED_Button>
          </CreditsForm>

          <HistoryContainer>
            <SectionTitle>Credit History ({totalCount} transactions)</SectionTitle>
            <Table
              dataSource={history}
              columns={historyColumns}
              rowKey="ledger_id"
              pagination={{ pageSize: 20 }}
              size="small"
            />
          </HistoryContainer>
        </>
      )}
    </div>
  );
};
